import React, { useEffect, useRef, useState } from "react";
import { GameService } from "../services/gameService";

export default function GameSelector({
  selectedGames = [],
  onChange,
  label = "Aktuelles Spiel",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const allGames = GameService.getAll();
  const normalizedSelection = GameService.normalizeSelectedGameIds(selectedGames);
  const summary = GameService.getSelectionSummary(normalizedSelection);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!wrapRef.current || wrapRef.current.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [open]);

  const updateSelection = (gameId) => {
    const nextSelection = GameService.toggleSelectedGameId(normalizedSelection, gameId);
    if (typeof onChange === "function") onChange(nextSelection);
  };

  return (
    <section
      ref={wrapRef}
      aria-label={label}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        marginBottom: 16,
        position: "relative",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        style={{
          width: "100%",
          minHeight: 58,
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 18,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)",
          color: "white",
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto",
          alignItems: "center",
          gap: 14,
          padding: "0 16px",
          cursor: "pointer",
          boxShadow: "0 14px 30px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.12)",
          boxSizing: "border-box",
          transition: "transform 120ms ease, background 160ms ease",
        }}
        onMouseDown={(event) => {
          event.currentTarget.style.transform = "scale(0.99)";
        }}
        onMouseUp={(event) => {
          event.currentTarget.style.transform = "scale(1)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.transform = "scale(1)";
        }}
      >
        <span
          style={{
            color: "rgba(255,255,255,0.7)",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>

        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "center",
            fontSize: 16,
            fontWeight: 1000,
            letterSpacing: 0.8,
            textTransform: "uppercase",
          }}
        >
          {summary}
        </span>

        <span
          aria-hidden="true"
          style={{
            color: "rgba(255,255,255,0.82)",
            fontSize: 18,
            lineHeight: 1,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 160ms ease",
          }}
        >
          ▾
        </span>
      </button>

      <div
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          opacity: open ? 1 : 0,
          transform: open ? "translateY(8px)" : "translateY(0)",
          transition: "grid-template-rows 180ms ease, opacity 150ms ease, transform 180ms ease",
          overflow: "hidden",
          position: "relative",
          zIndex: 20,
        }}
      >
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "rgba(14,8,47,0.98)",
              boxShadow: "0 18px 40px rgba(0,0,0,0.34)",
              padding: 8,
              display: "grid",
              gap: 6,
            }}
          >
            {allGames.map((game) => {
              const selected = normalizedSelection.includes(game.id);
              const selectable = GameService.isSelectable(game.id);
              const lockedSelected = selected && normalizedSelection.length === 1;

              return (
                <button
                  key={game.id}
                  type="button"
                  disabled={!selectable}
                  onClick={() => updateSelection(game.id)}
                  style={{
                    minHeight: 46,
                    border: "none",
                    borderRadius: 14,
                    background: selected ? "rgba(255,255,255,0.13)" : "rgba(255,255,255,0.04)",
                    color: selectable ? "white" : "rgba(255,255,255,0.36)",
                    display: "grid",
                    gridTemplateColumns: "30px minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 10,
                    padding: "0 12px",
                    cursor: selectable ? "pointer" : "default",
                    fontWeight: 1000,
                    letterSpacing: 0.7,
                    textTransform: "uppercase",
                    textAlign: "left",
                    opacity: selectable ? 1 : 0.55,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 7,
                      border: "1px solid rgba(255,255,255,0.28)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: selected
                        ? "linear-gradient(135deg, #ff2bd6 0%, #7a2bff 100%)"
                        : "rgba(0,0,0,0.18)",
                      color: "white",
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                    title={lockedSelected ? "Mindestens ein Spiel bleibt ausgewählt" : ""}
                  >
                    {selected ? "✓" : ""}
                  </span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {game.displayName}
                  </span>
                  {!selectable && game.availabilityLabel ? (
                    <span
                      style={{
                        color: "rgba(255,255,255,0.72)",
                        fontSize: 11,
                        fontWeight: 900,
                        letterSpacing: 0,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {game.availabilityLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
